import { LoanStatus } from '@willow/types-iso';

/**
 * A liquidation reversal restores the loan to its prior status. Foreclosure is excluded: it is
 * being retired as a loan status (it now lives in `configuration.foreclosures[]`) and is no longer
 * a status a loan can be liquidated from.
 */
export type LiquidatableLoanStatus = Exclude<LoanStatus, 'archived' | 'transferred' | 'paidOff' | 'liquidated'>;
export function isLiquidatableLoanStatus(status: LoanStatus): status is LiquidatableLoanStatus {
  return status !== 'archived' && status !== 'transferred' && status !== 'paidOff' && status !== 'liquidated';
}

export function isValidLoanStatusChange(
  status: LoanStatus,
  newStatus: LoanStatus,
  hasUntrackedPayoff: boolean,
  hasActiveBankruptcy: boolean,
): boolean {
  switch (status) {
    case 'preActive':
    case 'active':
      return hasActiveBankruptcy
        ? ['active', 'pendingTransfer', 'paidOff', 'archived', 'liquidated'].includes(newStatus)
        : true; // All options allowed
    case 'archived':
      return ['active'].includes(newStatus);
    case 'pendingActivation':
      return ['active', 'preActive', 'archived', 'liquidated'].includes(newStatus);
    case 'pendingTransfer':
      return ['preActive', 'active', 'transferred', 'paidOff', 'liquidated'].includes(newStatus);
    case 'transferred':
      return ['archived'].includes(newStatus);
    case 'liquidated':
      // Archived can be set directly. Every other transition out of liquidated restores the prior
      // status and must go through liquidation reversal (enforced in setLoanStatus).
      return newStatus === 'archived' || isLiquidatableLoanStatus(newStatus);
    case 'paidOff':
      // allow untracked payoffs to be reverted to active status
      const allowedStatuses = hasUntrackedPayoff ? ['active', 'archived'] : ['archived'];
      return allowedStatuses.includes(newStatus);
    case 'reo':
      // Note: there is future work needed when changing from reo to active status (i think its for reporting purposes?)
      // but while we were previously blocking this behavior in reality, we were just doing manual loan surgery to update the status
      return ['liquidated', 'archived', 'active'].includes(newStatus);
    default:
      return false;
  }
}

export function shouldRescindGoodbye(oldStatus: LoanStatus, newStatus: LoanStatus): boolean {
  return (oldStatus === 'pendingTransfer' || oldStatus === 'transferred') && ['active', 'paidOff'].includes(newStatus);
}
